import { Store, Parser, DataFactory, Writer } from 'n3';
import { JsonLdParser } from 'jsonld-streaming-parser';
import LinkHeader from 'http-link-header';

const parseTurtle = async (text, source = '') => {
    const store = new Store();
    return new Promise((resolve, reject) => {
        const parserOptions = {};
        if (source) {
            parserOptions.baseIRI = source;
        }
        const parser = new Parser(parserOptions);
        parser.parse(text, (error, quad) => {
            if (error) {
                reject(error);
            }
            if (quad) {
                store.add(DataFactory.quad(quad.subject, quad.predicate, quad.object, DataFactory.defaultGraph()));
            }
            else {
                resolve(store);
            }
        });
    });
};

const parseJsonld = async (text, source = '') => {
    const store = new Store();
    return new Promise((resolve, reject) => {
        const parser = new JsonLdParser({ baseIRI: source });
        parser.on('data', (quads) => store.add(quads));
        parser.on('error', (error) => reject(error));
        parser.on('end', () => resolve(store));
        parser.write(text);
        parser.end();
    });
};

const trimNamedGraph = (dataset) => {
    const newDataset = new Store();
    for (const q of dataset) {
        newDataset.add(DataFactory.quad(q.subject, q.predicate, q.object, DataFactory.defaultGraph()));
    }
    return newDataset;
};
const serializeTurtle = async (dataset, trim = false) => {
    const writer = new Writer({ format: 'text/turtle' });
    for (const quad of trim ? trimNamedGraph(dataset) : dataset) {
        writer.addQuad(quad);
    }
    return new Promise((resolve, reject) => {
        writer.end((error, text) => {
            if (error) {
                reject(error);
            }
            else {
                resolve(text);
            }
        });
    });
};

async function insertPatch(dataset) {
    return `INSERT DATA { ${await serializeTurtle(dataset)} }`;
}
async function deletePatch(dataset) {
    return `DELETE DATA { ${await serializeTurtle(dataset)} }`;
}

const getOneMatchingQuad = (dataset, subject, predicate, object, graph) => {
    const matches = dataset.match(subject, predicate, object, graph);
    return [...matches].shift();
};
const getAllMatchingQuads = (dataset, subject, predicate, object, graph) => [...dataset.match(subject, predicate, object, graph)];

async function unwrappedRdfFetch(whatwgFetch, iri, options) {
    let requestInit;
    if (options?.dataset) {
        const { dataset, ...request } = options;
        request.body = await serializeTurtle(options.dataset);
        request.headers = { 'Content-Type': 'text/turtle', ...request.headers };
        requestInit = request;
    }
    else {
        requestInit = { ...options };
        requestInit.headers = { Accept: 'text/turtle', ...requestInit.headers };
    }
    const response = await whatwgFetch(iri, requestInit);
    const rdfResponse = response.clone();
    rdfResponse.dataset = async function dataset() {
        const contentType = response.headers.get('Content-Type');
        if (contentType === null || !contentType.match('text/turtle')) {
            throw Error(`Content-Type was ${contentType}`);
        }
        return parseTurtle(await response.text(), response.url);
    };
    rdfResponse.text = response.text;
    return rdfResponse;
}
function fetchWrapper(whatwgFetch) {
    const wrappedRdfFetch = function wrappedRdfFetch(iri, options) {
        return unwrappedRdfFetch(whatwgFetch, iri, options);
    };
    wrappedRdfFetch.raw = whatwgFetch;
    return wrappedRdfFetch;
}

function getAgentRegistrationIri(linkHeaderText) {
    const links = LinkHeader.parse(linkHeaderText).refs;
    return links.find((link) => link.rel === 'http://www.w3.org/ns/solid/interop#registeredAgent')?.anchor;
}
function getDescriptionResource(linkHeaderText) {
    const links = LinkHeader.parse(linkHeaderText).refs;
    return links.find((link) => link.rel === 'describedby')?.uri;
}
function getStorageDescription(linkHeaderText) {
    const links = LinkHeader.parse(linkHeaderText).refs;
    return links.find((link) => link.rel === 'http://www.w3.org/ns/solid/terms#storageDescription')?.uri;
}
function targetDataRegistrationLink(dataRegistrationIri) {
    const link = new LinkHeader();
    link.set({
        uri: dataRegistrationIri,
        rel: 'http://www.w3.org/ns/solid/interop#targetDataRegistration'
    });
    return link.toString();
}

async function asyncIterableToArray(asyncIterable) {
    const arr = [];
    for await (const item of asyncIterable) {
        arr.push(item);
    }
    return arr;
}

function buildNamespace(base) {
    const handler = {
        get: (target, property) => DataFactory.namedNode(target.base + property)
    };
    return new Proxy({ base }, handler);
}
const INTEROP = buildNamespace('http://www.w3.org/ns/solid/interop#');
const NOTIFY = buildNamespace('http://www.w3.org/ns/solid/notifications#');
const RDF = buildNamespace('http://www.w3.org/1999/02/22-rdf-syntax-ns#');
const RDFS = buildNamespace('http://www.w3.org/2000/01/rdf-schema#');
const LDP = buildNamespace('http://www.w3.org/ns/ldp#');
const ACL = buildNamespace('http://www.w3.org/ns/auth/acl#');
const SHAPETREES = buildNamespace('http://www.w3.org/ns/shapetrees#');
const XSD = buildNamespace('http://www.w3.org/2001/XMLSchema#');
const SKOS = buildNamespace('http://www.w3.org/2004/02/skos/core#');
const ACP = buildNamespace('http://www.w3.org/ns/solid/acp#');
const SOLID = buildNamespace('http://www.w3.org/ns/solid/terms#');
const OIDC = buildNamespace('http://www.w3.org/ns/solid/oidc#');
const DC = buildNamespace('http://purl.org/dc/terms/');
const SPACE = buildNamespace('http://www.w3.org/ns/pim/space#');

class RequestError extends Error {
    constructor(message, response) {
        super(message);
        this.response = response;
    }
}
class AgentRegistrationDiscoveryError extends RequestError {
    constructor(response) {
        super('Agent Registration Discovery request failed', response);
        this.response = response;
    }
}
async function discoverAuthorizationAgent(webId, rdfFetch) {
    const userDataset = await (await rdfFetch(webId)).dataset();
    const authorizationAgentPattern = [DataFactory.namedNode(webId), INTEROP.hasAuthorizationAgent, null];
    return getOneMatchingQuad(userDataset, ...authorizationAgentPattern)?.object.value;
}
async function discoverAgentRegistration(authorizationAgentIri, fetch) {
    const response = await fetch(authorizationAgentIri, { method: 'HEAD' });
    if (!response.ok)
        throw new AgentRegistrationDiscoveryError(response);
    const linkHeader = response.headers.get('Link');
    if (!linkHeader)
        return undefined;
    return getAgentRegistrationIri(linkHeader);
}
async function discoverAuthorizationRedirectEndpoint(authorizationAgentIri, fetch) {
    const authzAgentDocumentResponse = await fetch(authorizationAgentIri, {
        headers: { Accept: 'application/ld+json' }
    });
    const doc = await parseJsonld(await authzAgentDocumentResponse.text(), authzAgentDocumentResponse.url);
    return getOneMatchingQuad(doc, null, INTEROP.hasAuthorizationRedirectEndpoint).object.value;
}

export { ACL, ACP, AgentRegistrationDiscoveryError, DC, INTEROP, LDP, NOTIFY, OIDC, RDF, RDFS, RequestError, SHAPETREES, SKOS, SOLID, SPACE, XSD, asyncIterableToArray, buildNamespace, deletePatch, discoverAgentRegistration, discoverAuthorizationAgent, discoverAuthorizationRedirectEndpoint, fetchWrapper, getAgentRegistrationIri, getAllMatchingQuads, getDescriptionResource, getOneMatchingQuad, getStorageDescription, insertPatch, parseJsonld, parseTurtle, serializeTurtle, targetDataRegistrationLink };
//# sourceMappingURL=index.mjs.map
